<template>
    <!-- Границы ширины: 28% хватает карточке на одну колонку значений, 50% — потолок,
         за которым от списка слева остаётся полоса. Прежние 35/80 позволяли увести
         таблицу почти целиком за край. Ширина в этих границах запоминается отдельно
         от панели заявки — см. App.vue. -->
    <Pane
        class="inventory-panel prevent-close"
        v-if="$route.params.id"
        :size="requestPaneSize"
        max-size="50"
        min-size="28"
    >
        <div class="inventory-panel__inner">
            <div class="inventory-panel__loader" v-if="isLoading">
                <VSpinner></VSpinner>
            </div>

            <p class="inventory-panel__error" v-else-if="error">{{ error }}</p>

            <template v-else-if="item">
                <!-- ШАПКА — две зоны, а не список полей: слева ЧТО это (превью, название,
                     состояние), справа ЧТО С ЭТИМ ДЕЛАТЬ (правка, закрытие, QR, печать
                     наклейки). Служебных реквизитов здесь нет намеренно: номер позиции,
                     город и серийный номер стоят в «Ключевой информации» ниже, и вторая
                     их копия мелким шрифтом делала шапку плотной, но не сильной.

                     Обе зоны переносятся целиком: сначала внутри левой превью уходит
                     на строку выше названия, и только потом правая опускается под левую.
                     Медиазапросами это не решить — ширина панели 28…50% области. -->
                <header class="inventory-panel__hero">
                    <div class="inventory-panel__identity">
                        <!-- ПРЕВЬЮ — ЦЕПОЧКА ИЗ ЧЕТЫРЁХ СТУПЕНЕЙ, от точного к общему:
                             1) главное фото ЭТОГО экземпляра — снимок той самой единицы
                                техники, которая лежит на складе;
                             2) типовое фото МОДЕЛИ — картинка шаблона, по которому единицу
                                завели (inventory_presets.preview_file_id);
                             3) рисунок по виду техники — догадка по названию, шесть видов;
                             4) плитка «изображения нет».
                             Сам порядок живёт не здесь, а в js/inventoryPreview.js
                             (resolvePreviewSource): лестницей v-else-if его нельзя ни
                             прочитать одним взглядом, ни проверить тестом без монтирования.

                             ФОТО ЭКЗЕМПЛЯРА ОТМЕНЯЕТ ТИПОВОЕ: снимок этой единицы точнее
                             картинки из каталога — на нём её наклейка, её скол и её
                             комплектация.

                             Пока байты едут, показывается нейтральная заглушка, а не рисунок
                             по виду: иначе при каждом открытии карточки рисунок мелькал бы
                             и сменялся снимком. Что фото есть, известно из item.primary_photo
                             и item.model_photo ещё до того, как приедут байты.

                             Размер плитки равен рамке QR: две равные по массе плитки
                             держат шапку по краям. -->
                        <div
                            class="inventory-panel__preview"
                            :class="{
                                'inventory-panel__preview_photo': Boolean(previewPhotoUrl),
                                'inventory-panel__preview_empty': isPreviewEmpty,
                            }"
                            :title="previewTitle"
                        >
                            <img
                                class="inventory-panel__preview-photo"
                                v-if="previewPhotoUrl"
                                :src="previewPhotoUrl"
                                :alt="previewPhotoName"
                            />

                            <span class="inventory-panel__preview-pending" v-else-if="isPreviewLoading"></span>

                            <component
                                class="inventory-panel__preview-image"
                                :is="previewComponent"
                                v-else-if="previewComponent"
                            ></component>

                            <NoPhotoSvg class="inventory-panel__preview-stub" v-else></NoPhotoSvg>
                        </div>

                        <div class="inventory-panel__identity-text">
                            <h2 class="inventory-panel__title">{{ item.name }}</h2>

                            <!-- Статус — второй по важности элемент карточки после названия
                                 и стоит прямо под ним. Цвет тот же, что у счётчиков списка
                                 и badge в таблице, а цвет текста считается от подложки:
                                 белым по жёлтому «В ремонте» не читается (js/readableColor.js). -->
                            <span class="inventory-panel__badge" :style="badgeStyle">{{ statusTitle }}</span>
                        </div>
                    </div>

                    <div class="inventory-panel__hero-side">
                        <div class="inventory-panel__hero-actions">
                            <!-- Права мало: нужен ещё город позиции. Наблюдатель из городской
                                 группы видит всю технику, а правит только свою — кнопка
                                 появляется лишь там, где сервер действительно даст сохранить. -->
                            <VButton
                                class="button_green-light inventory-panel__edit"
                                v-if="!isEditing && canEditCard"
                                @click="startEditing"
                            >
                                Редактировать
                            </VButton>

                            <!-- Закрытие — единственный выход из карточки: список под ней
                                 перекрыт. Красный акцент здесь не про опасность, а про
                                 заметность: бледный крестик рядом с зелёной кнопкой
                                 приходилось искать глазами. -->
                            <RouterLink
                                class="inventory-panel__close"
                                :to="{ name: 'inventory.items' }"
                                title="Закрыть карточку"
                            >
                                <TimesSvg></TimesSvg>
                            </RouterLink>
                        </div>

                        <!-- QR — рабочая утилита: по нему сверяют технику в руках с карточкой
                             на экране и печатают наклейку. Поэтому код крупный, читается
                             с телефона прямо с монитора, а скачивание — кнопка той же
                             ширины вплотную под ним: вместе они читаются одним блоком,
                             а не картинкой и случайной ссылкой рядом. -->
                        <div class="inventory-panel__qr" v-if="item.qr_code_url && !isEditing">
                            <a
                                class="inventory-panel__qr-frame"
                                :href="item.qr_code_url"
                                target="_blank"
                                title="Открыть QR-код в полном размере"
                            >
                                <img class="inventory-panel__qr-image" :src="item.qr_code_url" alt="QR-код" />
                            </a>

                            <a
                                class="inventory-panel__qr-download"
                                :href="item.qr_code_url"
                                target="_blank"
                                :download="qrFileName"
                                title="Скачать QR-код изображением"
                            >
                                <DownloadSvg class="inventory-panel__qr-download-icon"></DownloadSvg>
                                <span>Скачать QR</span>
                            </a>
                        </div>
                    </div>
                </header>

                <!-- Вкладок ровно три, и каждая за что-то отвечает. Заглушек под будущие
                     разделы (ремонты, связанные заявки) здесь нет намеренно: пустая вкладка
                     обещает функцию, которой нет, и отправляет пользователя искать её. -->
                <Tabs class="inventory-panel__tabs" :options="{ useUrlFragment: false }" ref="tabs">
                    <Tab id="main" name="Основное">
                        <!-- Просмотр: значения текстом, без полей ввода. Карточку открывают,
                             чтобы прочитать состояние предмета, а правят заметно реже. -->
                        <template v-if="!isEditing">
                            <!-- ВЕРХНЯЯ ЗОНА вкладки: слева паспорт предмета, справа его
                                 недавняя жизнь. Две колонки, пока панель их вмещает; уже —
                                 складываются друг под друга (flex-wrap, см. стили). -->
                            <div class="inventory-panel__columns">
                                <section
                                    class="inventory-panel__card inventory-panel__column inventory-panel__column_summary"
                                    v-if="summaryFactList.length"
                                >
                                    <h3 class="inventory-panel__section-title">Ключевая информация</h3>

                                    <dl class="inventory-panel__summary">
                                        <div
                                            class="inventory-panel__summary-row"
                                            v-for="summaryFact in summaryFactList"
                                            :key="summaryFact.label"
                                        >
                                            <dt class="inventory-panel__summary-name">{{ summaryFact.label }}</dt>
                                            <dd class="inventory-panel__summary-value">{{ summaryFact.value }}</dd>
                                        </div>
                                    </dl>
                                </section>

                                <!-- Компактный блок событий, а не лента во всю ширину: недавняя
                                     жизнь предмета — контекст к его описанию, а не его содержание.
                                     Полная лента — во вкладке «История». -->
                                <section
                                    class="inventory-panel__card inventory-panel__column inventory-panel__column_events inventory-panel__section_events"
                                >
                                    <div class="inventory-panel__card-head">
                                        <h3 class="inventory-panel__section-title">Последние события</h3>

                                        <button type="button" class="inventory-panel__card-link" @click="openHistory">
                                            Вся история
                                        </button>
                                    </div>

                                    <!-- Ссылка «Вся история» здесь своя, в заголовке блока,
                                         поэтому внутренняя в компактной ленте отключена. -->
                                    <VInventoryItemHistory
                                        compact
                                        :show-all-link="false"
                                        :key="`recent-${historyVersion}`"
                                        :item-id="item.id"
                                        :per-page="3"
                                    ></VInventoryItemHistory>
                                </section>
                            </div>

                            <!-- Ответственный — второй вопрос к карточке после «что это»:
                                 отдельный блок с именем во весь рост, а не строка среди
                                 технических полей. Пустое состояние названо словами. -->
                            <section class="inventory-panel__section">
                                <h3 class="inventory-panel__section-title">Ответственный</h3>

                                <div class="inventory-panel__card inventory-panel__holder">
                                    <div class="inventory-panel__holder-head">
                                        <span
                                            class="inventory-panel__holder-avatar"
                                            v-if="holderInitials"
                                            aria-hidden="true"
                                            >{{ holderInitials }}</span
                                        >

                                        <div class="inventory-panel__holder-box">
                                            <p
                                                class="inventory-panel__holder-name"
                                                :class="{ 'inventory-panel__holder-name_empty': !holder.assigned }"
                                            >
                                                {{ holder.name }}
                                            </p>

                                            <p class="inventory-panel__holder-kind" v-if="holder.kind">
                                                {{ holder.kind
                                                }}<span class="inventory-panel__holder-flag" v-if="holder.inactive">
                                                    · запись справочника погашена</span
                                                >
                                            </p>
                                        </div>
                                    </div>

                                    <dl class="inventory-panel__facts" v-if="usageFactList.length">
                                        <div
                                            class="inventory-panel__fact"
                                            v-for="usageFact in usageFactList"
                                            :key="usageFact.label"
                                        >
                                            <dt class="inventory-panel__fact-name">{{ usageFact.label }}</dt>
                                            <dd class="inventory-panel__fact-value">{{ usageFact.value }}</dd>
                                        </div>
                                    </dl>
                                </div>
                            </section>

                            <section class="inventory-panel__section" v-if="supplyFactList.length">
                                <h3 class="inventory-panel__section-title">Поставка и стоимость</h3>

                                <dl class="inventory-panel__facts inventory-panel__facts_tight">
                                    <div class="inventory-panel__fact" v-for="fact in supplyFactList" :key="fact.label">
                                        <dt class="inventory-panel__fact-name">{{ fact.label }}</dt>
                                        <dd class="inventory-panel__fact-value">
                                            {{ fact.value }}
                                            <span class="inventory-panel__fact-note" v-if="fact.note">{{
                                                fact.note
                                            }}</span>
                                        </dd>
                                    </div>
                                </dl>
                            </section>

                            <!-- Назначение и комментарий — свободный текст, ему нужна вся
                                 ширина. Блока нет, если у предмета нет такого атрибута:
                                 пустая рамка обещает данные, которых не существует. -->
                            <section
                                class="inventory-panel__section"
                                v-for="block in wideAttributeList"
                                :key="block.id"
                            >
                                <h3 class="inventory-panel__section-title">{{ block.label }}</h3>

                                <p class="inventory-panel__card inventory-panel__text">{{ block.value }}</p>
                            </section>

                            <section class="inventory-panel__section" v-if="extraAttributeList.length">
                                <h3 class="inventory-panel__section-title">Дополнительные данные</h3>

                                <dl class="inventory-panel__facts">
                                    <div
                                        class="inventory-panel__fact"
                                        v-for="attribute in extraAttributeList"
                                        :key="attribute.id"
                                    >
                                        <dt class="inventory-panel__fact-name">{{ attribute.type }}</dt>
                                        <dd class="inventory-panel__fact-value">
                                            {{ attribute.value || emptyValue }}
                                        </dd>
                                    </div>
                                </dl>
                            </section>

                            <!-- БЫСТРЫЕ ДЕЙСТВИЯ — БИЗНЕС-ОПЕРАЦИИ, И ТОЛЬКО ОНИ (7-F2B).
                                 Ручного селекта статуса здесь больше нет: статус — результат
                                 действия, а не поле. Выбрать «Списано» списком значило бы
                                 списать технику без причины, без автора и без записи
                                 в истории — незаметно для того, кто выбирал.

                                 Показываются только те действия, что имеют смысл в текущем
                                 состоянии; серых кнопок, на которые нельзя нажать, нет
                                 намеренно. У списанного оборудования их не остаётся вовсе —
                                 карточка и история при этом открыты на чтение.

                                 Каждое действие открывает своё окно и уходит отдельным
                                 маршрутом: состояние предмета и событие деловой истории
                                 меняются одной транзакцией на сервере. -->
                            <section
                                class="inventory-panel__section inventory-panel__section_actions"
                                v-if="canEditItem && itemActions.length"
                            >
                                <h3 class="inventory-panel__section-title">Быстрые действия</h3>

                                <div class="inventory-panel__quick-buttons">
                                    <VButton
                                        type="button"
                                        class="inventory-panel__quick-button"
                                        :class="actionClass(action)"
                                        :key="action"
                                        v-for="action in itemActions"
                                        :disabled="isSaving"
                                        @click="runAction(action)"
                                    >
                                        <component
                                            class="inventory-panel__quick-icon"
                                            :is="actionIcon(action)"
                                        ></component>
                                        <span>{{ actionTitle(action) }}</span>
                                    </VButton>
                                </div>
                            </section>
                        </template>

                        <!-- Редактирование: та же форма, что и при создании оборудования.
                             Просмотр и правка остаются двумя разными видами карточки —
                             значения по месту здесь не правятся. -->
                        <ValidationObserver v-slot="{ handleSubmit }" v-else>
                            <form class="active-form" @submit.prevent="handleSubmit(save)">
                                <!-- СТАТУСА В ЭТОЙ ФОРМЕ БОЛЬШЕ НЕТ (7-F2B). Он результат
                                     бизнес-операции, а не поле карточки: «В эксплуатации»
                                     ставит выдача, «На складе» — возврат, «В ремонте» —
                                     отправка в ремонт, «Списано» — списание. Список статусов
                                     позволял поставить любой из них мимо всей этой записи.

                                     ДЕРЖАТЕЛЯ И ГОРОДА В ЭТОЙ ФОРМЕ БОЛЬШЕ НЕТ (7-F2A).
                                     Их изменение — события в жизни вещи, а не правка полей:
                                     у выдачи, возврата и переезда есть автор, причина
                                     и след в истории, которого свободная правка не оставляла.
                                     Сервер отвергает смену города через это сохранение,
                                     а держателя оно не касалось и раньше.

                                     Остальные поля карточки правятся как прежде. -->
                                <VInventoryItemFields
                                    hide-holder
                                    hide-city
                                    :item="item"
                                    :cache="cache"
                                ></VInventoryItemFields>

                                <div class="inventory-panel__actions">
                                    <VButton type="submit" class="button_green" :disabled="isSaving">
                                        {{ isSaving ? 'Сохраняем…' : 'Сохранить' }}
                                    </VButton>

                                    <VButton
                                        type="button"
                                        class="button_green-light"
                                        :disabled="isSaving"
                                        @click="cancelEditing"
                                    >
                                        Отмена
                                    </VButton>

                                    <!-- Кнопки «Снять ответственного» здесь больше нет:
                                         держателя меняют бизнес-действия «Выдать», «Вернуть»
                                         и «Сменить владельца» — только они оставляют
                                         в истории запись о том, кто и когда это сделал. -->
                                </div>
                            </form>
                        </ValidationObserver>
                    </Tab>

                    <!-- ВКЛАДКА «ИСТОРИЯ» ОТВЕЧАЕТ НА ДВА РАЗНЫХ ВОПРОСА, и с 7-F1 они
                         разведены. «Что происходило с вещью» — приняли, выдали, увезли,
                         списали — это деловая история, и она главная: её читает кладовщик
                         и руководитель. «Кто какое поле поменял» — технический журнал,
                         он нужен поддержке и стоит ниже, свёрнутым.

                         Свёрнутым, а не удалённым: журнал остаётся единственным местом,
                         где видно правку атрибута или прикрепление файла, и его API
                         не менялось. -->
                    <Tab id="history" name="История">
                        <section class="inventory-panel__timeline">
                            <h3 class="inventory-panel__tab-title">История оборудования</h3>

                            <VInventoryItemLifecycle
                                :key="`lifecycle-${historyVersion}`"
                                :item-id="item.id"
                            ></VInventoryItemLifecycle>
                        </section>

                        <section class="inventory-panel__audit">
                            <!-- Кнопка, а не наведение и не вечно раскрытый блок:
                                 техническая лента длиннее деловой в десятки раз, и
                                 развёрнутая по умолчанию она сделала бы главным то,
                                 что второстепенно. Запрос за ней уходит только после
                                 раскрытия — см. v-if ниже. -->
                            <button
                                type="button"
                                class="inventory-panel__audit-toggle"
                                :aria-expanded="String(isAuditVisible)"
                                @click="toggleAudit"
                            >
                                <span class="inventory-panel__audit-caret" aria-hidden="true">
                                    {{ isAuditVisible ? '▾' : '▸' }}
                                </span>

                                Техническая история
                            </button>

                            <VInventoryItemHistory
                                v-if="isAuditVisible"
                                :key="`full-${historyVersion}`"
                                :item-id="item.id"
                            ></VInventoryItemHistory>
                        </section>
                    </Tab>

                    <!-- Вложения отдельной вкладкой, а не блоком в «Основном»: фотографий
                         у предмета бывает два десятка, и в паспорте они вытеснили бы сам
                         паспорт. Право правки передаётся готовым — компонент вложений
                         ролей и групп не знает и доступ сам не считает. -->
                    <Tab id="files" name="Файлы">
                        <VInventoryItemFiles
                            :item-id="item.id"
                            :can-edit="canEditItem"
                            @changed="afterChange"
                            @primary="onPrimaryPhoto"
                        ></VInventoryItemFiles>
                    </Tab>

                    <!-- Обращения Service Desk по этой единице (7-G1). Только чтение и только
                         обращения, которые пользователь может открыть; привязка — из обращения. -->
                    <Tab id="requests" name="Обращения">
                        <VInventoryItemRequests :item-id="item.id"></VInventoryItemRequests>
                    </Tab>
                </Tabs>
            </template>
        </div>

        <!-- Окна бизнес-операций живут здесь, а не в списке: они про КОНКРЕТНЫЙ предмет,
             и справочники держателей и городов у карточки уже загружены. -->
        <VInventoryItemHolderActionModal :cache="cache"></VInventoryItemHolderActionModal>
        <VInventoryItemReturnModal></VInventoryItemReturnModal>
        <VInventoryItemMoveModal :cache="cache"></VInventoryItemMoveModal>
        <VInventoryItemRepairModal></VInventoryItemRepairModal>
        <VInventoryItemRepairReturnModal></VInventoryItemRepairReturnModal>
        <VInventoryItemWriteOffModal></VInventoryItemWriteOffModal>
        <VInventoryItemMarkBrokenModal></VInventoryItemMarkBrokenModal>
        <VInventoryItemClearFaultModal></VInventoryItemClearFaultModal>
    </Pane>
</template>
<script>
// svg
import TimesSvg from '@/images/times.svg';
import DownloadSvg from '@/images/download.svg';
import LaptopSvg from '@/images/inventory/laptop.svg';
import MonitorSvg from '@/images/inventory/monitor.svg';
import HubSvg from '@/images/inventory/hub.svg';
import DesktopSvg from '@/images/inventory/desktop.svg';
import PrinterSvg from '@/images/inventory/printer.svg';
import PhoneSvg from '@/images/inventory/phone.svg';
import NoPhotoSvg from '@/images/inventory/no-photo.svg';
import RepairSvg from '@/images/inventory/repair.svg';
import UserCheckSvg from '@/images/inventory/user-check.svg';
import UserMinusSvg from '@/images/inventory/user-minus.svg';
import TruckSvg from '@/images/inventory/truck.svg';

// Components
import { Pane } from 'splitpanes';
import { Tabs, Tab } from 'vue-tabs-component';
import VButton from '../../components/VButton/VButton.vue';
import VSelect from '../../components/VSelect/VSelect.vue';
import VSpinner from '../../components/VProgress/VSpinner.vue';
import VInventoryItemFields from './components/VInventoryItemFields.vue';
import VInventoryItemFiles from './components/VInventoryItemFiles.vue';
import VInventoryItemRequests from './components/VInventoryItemRequests.vue';
import VInventoryItemHistory from './components/VInventoryItemHistory.vue';
import VInventoryItemLifecycle from './components/VInventoryItemLifecycle.vue';
import VInventoryItemHolderActionModal from './modals/VInventoryItemHolderActionModal.vue';
import VInventoryItemReturnModal from './modals/VInventoryItemReturnModal.vue';
import VInventoryItemMoveModal from './modals/VInventoryItemMoveModal.vue';
import VInventoryItemRepairModal from './modals/VInventoryItemRepairModal.vue';
import VInventoryItemRepairReturnModal from './modals/VInventoryItemRepairReturnModal.vue';
import VInventoryItemWriteOffModal from './modals/VInventoryItemWriteOffModal.vue';
import VInventoryItemMarkBrokenModal from './modals/VInventoryItemMarkBrokenModal.vue';
import VInventoryItemClearFaultModal from './modals/VInventoryItemClearFaultModal.vue';
import { ValidationObserver } from 'vee-validate';

// Other
import Vue from 'vue';
import Component from 'vue-class-component';
import { namespace } from 'vuex-class';
import { Prop, Watch } from 'vue-property-decorator';
import { errorMessage } from '../../../js/error';
import ModalNames from '../../../js/enums/ModalNames';
import InventoryItemStatusTitle from '../../../js/enums/InventoryItemStatusTitle';
import InventoryItemStatusColor from '../../../js/enums/InventoryItemStatusColor';
import InventoryItemStatusType from '../../../js/enums/InventoryItemStatusType';
import { EMPTY_VALUE } from '../../../js/inventoryAttributes';
import {
    assetSummary,
    extraFacts,
    holderInfo,
    holderInitials as initialsOf,
    supplyFacts,
    usageFacts,
    wideAttributeBlocks,
} from '../../../js/inventoryCard';
import {
    DEVICE_DESKTOP,
    DEVICE_HUB,
    DEVICE_LAPTOP,
    DEVICE_MONITOR,
    DEVICE_PHONE,
    DEVICE_PRINTER,
    PREVIEW_DEVICE_KIND,
    PREVIEW_EMPTY,
    PREVIEW_ITEM_PHOTO,
    PREVIEW_MODEL_PHOTO,
    detectDeviceKind,
    isPreviewPending,
    resolvePreviewSource,
} from '../../../js/inventoryPreview';
import { readableTextOn, rgbaOf } from '../../../js/readableColor';
import {
    ACTION_CHANGE_HOLDER,
    ACTION_CLEAR_FAULT,
    ACTION_ISSUE,
    ACTION_MARK_BROKEN,
    ACTION_MOVE,
    ACTION_RETURN,
    ACTION_RETURN_FROM_REPAIR,
    ACTION_SEND_TO_REPAIR,
    ACTION_WRITE_OFF,
    actionTitle,
    availableItemActions,
    isDestructiveAction,
} from '../../../js/inventoryActions';
import {
    downloadInventoryPresetPreview,
    findInventoryItem,
    updateInventoryItem,
    getAllInventoryUsers,
    paginateInventoryHolders,
    getInventoryAttributeTypes,
    getInventorySourceData,
} from '../../../js/inventory-api';

const Workspace = namespace('workspace');

/**
 * Карточка оборудования в боковой панели рядом со списком.
 *
 * Заменяет прежнюю модалку: список остаётся виден, и по нему можно ходить, не закрывая
 * карточку. Механизм — тот же именованный слот sharedPanel, которым открывается заявка
 * (см. App.vue и RequestView): компонент сам является Pane и принимает его ширину.
 *
 * Имя пропа requestPaneSize менять нельзя — оно задано в App.vue, общем для всего
 * приложения, и переименование потребовало бы трогать раскладку заявок.
 */
@Component({
    components: {
        TimesSvg,
        DownloadSvg,
        LaptopSvg,
        MonitorSvg,
        HubSvg,
        DesktopSvg,
        PrinterSvg,
        PhoneSvg,
        NoPhotoSvg,
        RepairSvg,
        UserCheckSvg,
        UserMinusSvg,
        TruckSvg,
        Pane,
        Tabs,
        Tab,
        VButton,
        VSelect,
        VSpinner,
        VInventoryItemFields,
        VInventoryItemFiles,
        VInventoryItemRequests,
        VInventoryItemHistory,
        VInventoryItemLifecycle,
        VInventoryItemHolderActionModal,
        VInventoryItemReturnModal,
        VInventoryItemMoveModal,
        VInventoryItemRepairModal,
        VInventoryItemRepairReturnModal,
        VInventoryItemWriteOffModal,
        VInventoryItemMarkBrokenModal,
        VInventoryItemClearFaultModal,
        ValidationObserver,
    },
})
class InventoryItemPanel extends Vue {
    @Workspace.Mutation SET_INVENTORY_SCOPE;

    @Prop({ type: Number, default: 40 })
    requestPaneSize;

    // Справочники панель грузит сама, а не получает от списка: она отдельный компонент
    // маршрута в слоте sharedPanel, и props ей передаёт App.vue — общий для всего
    // приложения, про инвентарь он ничего не знает и знать не должен.
    //
    // Пустые коллекции, а не null: vue-select зовёт options.concat(), а VAttribute
    // обращается к attributesMap по ключу — и то и другое на null падает ещё до того,
    // как справочники доедут.
    cache = {
        users: [],
        holders: [],
        attributes: [],
        attributesMap: {},
        attributesTypes: [],
        cities: [],
    };

    item = null;
    isLoading = false;
    isSaving = false;
    error = '';

    // Панель открывается на чтение: карточку смотрят заметно чаще, чем правят.
    isEditing = false;

    // Снимок предмета на момент входа в редактирование. Нужен «Отмене»: VInventoryItemFields
    // правит объект на месте, поэтому вернуть прежние значения можно только из копии.
    snapshot = null;

    // Ключ для перерисовки ленты: история не умеет обновляться сама, а после сохранения
    // в ней появляются новые события. Смена ключа пересоздаёт компонент и перезапрашивает.
    historyVersion = 0;

    /**
     * Раскрыта ли техническая история.
     *
     * Свёрнута по умолчанию, и это не только про место на экране: пока блок свёрнут,
     * запроса за журналом не уходит вовсе (v-if, а не v-show). На карточке, которую
     * открывают, чтобы посмотреть, у кого сейчас ноутбук, лишняя страница журнала
     * не нужна никому.
     */
    isAuditVisible = false;

    /**
     * Главное фото для шапки: {id, url} или null.
     *
     * ПРИХОДИТ ОТ ВКЛАДКИ «ФАЙЛЫ», а не запрашивается здесь. У файлов оборудования нет
     * открытого адреса — содержимое приезжает запросом с токеном и живёт как object URL,
     * и вкладка всё равно забирает его для плитки. Второй запрос за теми же байтами
     * при каждом открытии карточки был бы платой за независимость, которая никому не нужна:
     * вкладка живёт ровно столько же, сколько шапка (см. v-show у вкладок).
     */
    primaryPhoto = null;

    /**
     * Сказала ли вкладка своё слово. До этого о наличии фото судим по item.primary_photo,
     * после — по сказанному: иначе удаление главного фото оставляло бы шапку ждать кадр,
     * которого больше нет, — карточка-то не перечитывалась.
     */
    primaryPhotoKnown = false;

    /**
     * Типовое фото модели: временная ссылка на содержимое или пустая строка.
     *
     * ЗАПРАШИВАЕТСЯ ЗДЕСЬ, в отличие от главного фото. Главное приходит от вкладки
     * «Файлы» — она всё равно забирает его для своей плитки, и второй запрос за теми же
     * байтами был бы лишним. Картинку ШАБЛОНА не показывает никто, кроме этой шапки,
     * и просить её больше некого.
     */
    modelPhotoUrl = '';

    /**
     * Просили ли уже содержимое типового фото. Признак нужен, чтобы не запрашивать
     * его повторно: шапка решает, нужна ли картинка, дважды — при открытии карточки
     * и когда вкладка сообщает, что главного фото больше нет.
     */
    modelPhotoRequested = false;

    /**
     * Номер текущего чтения карточки. Панель одна на все предметы: при переходе A -> B
     * она не пересоздаётся, а перечитывает данные, и ответ для A может приехать последним.
     * Применяется только ответ с текущим номером — опоздавший не перезапишет открытый предмет.
     */
    itemLoadToken = 0;

    /**
     * Номер текущего запроса типового фото. Растёт при каждом сбросе фото, и кадр,
     * запрошенный до сброса, по возвращении узнаёт, что опоздал: фото в шапке обязано
     * принадлежать открытому предмету, а не тому, что был открыт секундой раньше.
     */
    modelPhotoToken = 0;

    /**
     * Можно ли править именно эту позицию: право правки И её город.
     *
     * Решение по канонической колонке city_id. Даже если в карточке виден город из старого
     * атрибута, при пустом city_id позиция городскому пользователю доступна только на чтение —
     * ровно так же отвечает бэкенд.
     */
    get canEditItem() {
        return this.$_canEditInventoryItem(this.item);
    }

    /**
     * Правка самой карточки: сверх права — ещё и не списанная вещь. Списанное доступно
     * только для просмотра, и сервер правку отклонит; вложения при этом остаются
     * на canEditItem — акт списания прикладывают как раз после списания.
     */
    get canEditCard() {
        return this.canEditItem && Number(this.item?.status) !== InventoryItemStatusType.DECOMMISSIONED;
    }

    get itemId() {
        const id = parseInt(this.$route.params.id, 10);

        return id && !isNaN(id) ? id : null;
    }

    get hasHolder() {
        return Boolean(this.item && (this.item.responsible_person_id || this.item.holder_id));
    }

    get statusTitle() {
        return InventoryItemStatusTitle[this.item?.status] ?? EMPTY_VALUE;
    }

    get statusColor() {
        return InventoryItemStatusColor[this.item?.status] ?? '#9aa0a6';
    }

    /**
     * Оформление пилюли статуса: подложка, читаемый на ней текст и мягкая тень её же цвета.
     *
     * Цвет текста считается, а не задан константой: цвета статусов выбраны по смыслу,
     * и среди них есть почти жёлтый #f2c94c, на котором белый текст не виден. Подробности
     * и порог — в js/readableColor.js.
     */
    get badgeStyle() {
        const color = this.statusColor;

        return {
            '--badge-color': color,
            '--badge-text': readableTextOn(color),
            '--badge-shadow': rgbaOf(color, 0.35),
        };
    }

    get emptyValue() {
        return EMPTY_VALUE;
    }

    /**
     * Блоки карточки. Что и когда показывать — в js/inventoryCard.js: там же это
     * и проверяется тестами, потому что сам компонент в тестах не монтируется.
     */
    get summaryFactList() {
        return assetSummary(this.item);
    }

    /**
     * Есть ли у предмета главное фото. До первого слова вкладки — по карточке,
     * после — по сказанному ею.
     */
    get hasPrimaryPhoto() {
        return this.primaryPhotoKnown ? Boolean(this.primaryPhoto) : Boolean(this.item && this.item.primary_photo);
    }

    /**
     * Адрес содержимого главного фото. Пустая строка, пока кадр не приехал.
     */
    get primaryPhotoUrl() {
        return this.primaryPhoto ? this.primaryPhoto.url : '';
    }

    /**
     * Подпись к снимку. Имя файла — не лучший alt, но единственное, что про этот кадр
     * известно; название предмета описывало бы предмет, а не изображение.
     */
    get primaryPhotoName() {
        return this.item && this.item.primary_photo ? this.item.primary_photo.name : 'Фотография оборудования';
    }

    /**
     * Есть ли у модели типовое фото. Известно из ответа карточки — до того, как поедут
     * байты, и потому цепочка не мигает.
     */
    get hasModelPhoto() {
        return Boolean(this.item && this.item.model_photo);
    }

    /**
     * Какая ступень цепочки сейчас показывается. Правило — в js/inventoryPreview.js,
     * там же оно и проверяется тестами.
     */
    get previewSource() {
        return resolvePreviewSource({
            hasItemPhoto: this.hasPrimaryPhoto,
            hasModelPhoto: this.hasModelPhoto,
            deviceKind: this.previewKind,
        });
    }

    /**
     * Адрес содержимого выбранной ступени. Пустая строка — картинка ещё едет либо
     * ступень вовсе не фотографическая.
     */
    get previewPhotoUrl() {
        if (this.previewSource === PREVIEW_ITEM_PHOTO) {
            return this.primaryPhotoUrl;
        }

        return this.previewSource === PREVIEW_MODEL_PHOTO ? this.modelPhotoUrl : '';
    }

    /**
     * Нейтральная заглушка вместо рисунка по виду, пока едет фотография. Рисунок,
     * показанный на эти доли секунды, сменился бы снимком — это и есть мигание.
     */
    get isPreviewLoading() {
        return isPreviewPending(this.previewSource, this.previewPhotoUrl);
    }

    /**
     * Подпись к картинке. Имя файла — не лучший alt, но единственное, что про этот кадр
     * известно; название предмета описывало бы предмет, а не изображение.
     */
    get previewPhotoName() {
        if (this.previewSource === PREVIEW_MODEL_PHOTO) {
            return this.item.model_photo.name || 'Типовое изображение модели';
        }

        return this.primaryPhotoName;
    }

    /**
     * Пунктирная плитка «изображения нет» — только на последней ступени цепочки.
     */
    get isPreviewEmpty() {
        return this.previewSource === PREVIEW_EMPTY;
    }

    /**
     * Вид устройства для превью — догадка по названию, см. js/inventoryPreview.js.
     */
    get previewKind() {
        return detectDeviceKind(this.item);
    }

    /**
     * Иллюстрация под распознанный вид.
     *
     * Картами, а не цепочкой v-else-if: видов шесть, и в шаблоне такая цепочка занимала бы
     * больше места, чем вся остальная шапка. Не распознали — null, и на плитке
     * оказывается «изображения нет».
     */
    get previewComponent() {
        // Только на своей ступени: на остальных рисунок не нужен, а разметка иначе
        // проверяла бы порядок вторично и своим способом.
        if (this.previewSource !== PREVIEW_DEVICE_KIND) {
            return null;
        }

        const images = {
            [DEVICE_LAPTOP]: LaptopSvg,
            [DEVICE_MONITOR]: MonitorSvg,
            [DEVICE_HUB]: HubSvg,
            [DEVICE_DESKTOP]: DesktopSvg,
            [DEVICE_PRINTER]: PrinterSvg,
            [DEVICE_PHONE]: PhoneSvg,
        };

        return images[this.previewKind] ?? null;
    }

    get previewTitle() {
        // Снимок в подписи не нуждается в оговорках: это фотография именно этой единицы
        // техники, а не догадка по названию, и говорить о ней надо иначе.
        if (this.previewSource === PREVIEW_ITEM_PHOTO) {
            return this.primaryPhotoUrl ? 'Главное фото оборудования' : 'Главное фото загружается…';
        }

        // Про типовое сказано отдельно и честно: это картинка МОДЕЛИ, а не снимок этой
        // единицы, и человек у стеллажа должен понимать разницу — иначе он сверит скол
        // на картинке с целым корпусом перед собой и решит, что перепутал технику.
        if (this.previewSource === PREVIEW_MODEL_PHOTO) {
            return this.modelPhotoUrl ? 'Типовое изображение модели' : 'Типовое изображение загружается…';
        }

        const titles = {
            [DEVICE_LAPTOP]: 'Ноутбук',
            [DEVICE_MONITOR]: 'Монитор',
            [DEVICE_HUB]: 'Хаб или док-станция',
            [DEVICE_DESKTOP]: 'Системный блок',
            [DEVICE_PRINTER]: 'Принтер или МФУ',
            [DEVICE_PHONE]: 'Телефон',
        };

        // Про догадку сказано словами: подпись объясняет и саму картинку, и её отсутствие.
        return titles[this.previewKind] ?? 'Изображения нет: вид оборудования не распознан';
    }

    /**
     * Имя файла QR-кода при скачивании.
     *
     * С номером предмета: наклейки печатают пачками, и десять файлов qr-code.png
     * в загрузках различить нельзя.
     */
    get qrFileName() {
        return this.item ? `qr-${this.item.id}.png` : 'qr-code.png';
    }

    get holder() {
        return holderInfo(this.item);
    }

    get holderInitials() {
        return this.holder.assigned ? initialsOf(this.holder.name) : '';
    }

    get usageFactList() {
        return usageFacts(this.item, (value) => this.formatDate(value));
    }

    get supplyFactList() {
        return supplyFacts(this.item, (value) => this.formatDate(value));
    }

    /**
     * Широкие блоки свободного текста — только под реально существующие атрибуты.
     */
    get wideAttributeList() {
        return wideAttributeBlocks(this.item);
    }

    get extraAttributeList() {
        return extraFacts(this.item);
    }

    /**
     * Есть ли куда отправлять в ремонт: позиция уже в ремонте — кнопки нет.
     */
    /**
     * Бизнес-действия, которые имеет смысл предложить для этой позиции.
     *
     * Правило — в js/inventoryActions.js: там оно и проверяется тестами. Право правки
     * именно этой позиции (разрешение плюс её город) передаётся туда же — кнопка,
     * которую сервер всё равно отвергнет, хуже отсутствующей.
     */
    get itemActions() {
        return availableItemActions(this.item, this.canEditItem);
    }

    /**
     * Иконка действия. Карта, а не цепочка v-else-if: действий четыре, и в шаблоне
     * такая цепочка занимала бы больше места, чем сам блок.
     *
     * @param {string} action
     * @returns {Object}
     */
    actionIcon(action) {
        const icons = {
            [ACTION_ISSUE]: UserCheckSvg,
            [ACTION_CHANGE_HOLDER]: UserCheckSvg,
            [ACTION_RETURN]: UserMinusSvg,
            [ACTION_MOVE]: TruckSvg,
            [ACTION_SEND_TO_REPAIR]: RepairSvg,
            [ACTION_RETURN_FROM_REPAIR]: RepairSvg,
            [ACTION_MARK_BROKEN]: RepairSvg,
            [ACTION_CLEAR_FAULT]: RepairSvg,
            [ACTION_WRITE_OFF]: TimesSvg,
        };

        return icons[action] ?? UserCheckSvg;
    }

    /**
     * Оформление кнопки действия.
     *
     * Списание отличается цветом ещё до нажатия: остальные операции обратимы другой
     * операцией, а эта — нет, и узнавать об этом из окна поздновато.
     *
     * @param {string} action
     * @returns {string}
     */
    actionClass(action) {
        return isDestructiveAction(action) ? 'inventory-panel__quick-button_danger' : 'button_green-light';
    }

    /**
     * @param {string} action
     * @returns {string}
     */
    actionTitle(action) {
        return actionTitle(action);
    }

    /**
     * Имя текущего держателя — для окна возврата: оно показывает, у кого вещь сейчас.
     */
    get holderName() {
        return this.holder.assigned ? this.holder.name : '';
    }

    formatDate(value) {
        return value ? this.$_toLocalDateHistory(value) : EMPTY_VALUE;
    }

    startEditing() {
        // Вторая застава помимо скрытой кнопки: в режим правки нельзя попасть
        // ни программно, ни из другого места компонента.
        if (!this.canEditCard) {
            return;
        }

        // Глубокая копия: у предмета есть массив атрибутов, и поверхностная оставила бы
        // форме тот же массив — правки в нём пережили бы «Отмену».
        this.snapshot = JSON.parse(JSON.stringify(this.item));
        this.isEditing = true;
    }

    // Отмена ничего не отправляет на сервер: просто возвращает предмет к снимку.
    cancelEditing() {
        if (this.snapshot) {
            this.item = JSON.parse(JSON.stringify(this.snapshot));
        }

        this.snapshot = null;
        this.isEditing = false;
    }

    toggleAudit() {
        this.isAuditVisible = !this.isAuditVisible;
    }

    openHistory() {
        const tabs = this.$refs.tabs;

        if (tabs && typeof tabs.selectTab === 'function') {
            tabs.selectTab('#history');
        }
    }

    /**
     * Вкладка «Файлы» сообщила, какое фото сейчас главное.
     *
     * Ссылка чужая: её создала и отпустит вкладка. Здесь она только показывается, и
     * отзывать её отсюда нельзя — вкладка сама скажет null, прежде чем отпустить.
     *
     * @param {{id: number, url: string}|null} photo
     */
    onPrimaryPhoto(photo) {
        // Пока карточка перечитывается, вкладка прежнего предмета уничтожается и напоследок
        // говорит «главного фото нет». К открываемому предмету это не относится: приняв
        // это слово, шапка запросила бы типовое фото ПРЕЖНЕГО предмета и показала бы его
        // в новой карточке — так док-станция и получала фото наушников.
        if (this.isLoading) {
            return;
        }

        this.primaryPhoto = photo && photo.url ? photo : null;
        this.primaryPhotoKnown = true;

        // Главное фото удалили прямо сейчас — шапка переходит к следующей ступени,
        // и типовое фото модели может понадобиться впервые за это открытие карточки.
        this.ensureModelPhoto();
    }

    /**
     * Забрать содержимое типового фото модели, если оно нужно и ещё не запрошено.
     *
     * НУЖНО ОНО НЕ ВСЕГДА: при живом главном фото экземпляра картинка модели не
     * показывается вовсе, и тянуть её за каждое открытие карточки значит платить
     * мобильным трафиком за то, чего никто не увидит.
     *
     * Ошибка тихая: на месте картинки останется рисунок по виду техники, а модалка
     * поверх карточки из-за не приехавшего превью — это несоразмерно.
     */
    async ensureModelPhoto() {
        if (this.modelPhotoRequested || this.hasPrimaryPhoto || !this.hasModelPhoto) {
            return;
        }

        // В карточке ещё прежний предмет, а открыт уже другой: свой кадр новый предмет
        // запросит сам, когда приедет.
        if (this.item.id !== this.itemId) {
            return;
        }

        const token = this.modelPhotoToken;
        const itemId = this.item.id;
        const presetId = this.item.model_photo.preset_id;
        this.modelPhotoRequested = true;

        try {
            const response = await downloadInventoryPresetPreview(presetId);

            // Карточку успели закрыть или переключить, пока кадр ехал: чужой кадр
            // в шапку не попадает.
            if (!this.isCurrentModelPhoto(token, itemId, presetId)) {
                return;
            }

            this.modelPhotoUrl = URL.createObjectURL(response.data);
        } catch (requestError) {
            // Опоздавшая ошибка тоже чужая: стерев ссылку, она убрала бы уже приехавший
            // кадр открытого предмета.
            if (this.isCurrentModelPhoto(token, itemId, presetId)) {
                this.modelPhotoUrl = '';
            }
        }
    }

    /**
     * Относится ли приехавший кадр к тому, что открыто сейчас: запрос не устарел,
     * и в карточке тот же предмет с тем же шаблоном.
     *
     * @param {number} token
     * @param {number} itemId
     * @param {number} presetId
     * @returns {boolean}
     */
    isCurrentModelPhoto(token, itemId, presetId) {
        return (
            token === this.modelPhotoToken &&
            Boolean(this.item) &&
            this.item.id === itemId &&
            this.hasModelPhoto &&
            this.item.model_photo.preset_id === presetId
        );
    }

    /**
     * Отпустить временную ссылку на типовое фото.
     *
     * Object URL держит содержимое в памяти вкладки, пока его не отозвали, и живёт
     * до перезагрузки страницы. Карточка открывается десятки раз за смену.
     *
     * Заодно делает устаревшими все запросы кадра, отправленные до сброса.
     */
    releaseModelPhoto() {
        this.modelPhotoToken += 1;

        if (this.modelPhotoUrl) {
            URL.revokeObjectURL(this.modelPhotoUrl);
        }

        this.modelPhotoUrl = '';
        this.modelPhotoRequested = false;
    }

    async fetchItem() {
        this.itemLoadToken += 1;

        const token = this.itemLoadToken;
        const itemId = this.itemId;

        if (!itemId) {
            this.item = null;
            this.isLoading = false;
            return;
        }

        // Карточка перечитывается — вкладка «Файлы» пересоздаётся вместе со всем поддеревом
        // (оно под v-else-if="item"), и её прежние ссылки уже недействительны. Ждём слова
        // заново, иначе шапка показывала бы отпущенный кадр или пустоту вместо рисунка.
        this.primaryPhoto = null;
        this.primaryPhotoKnown = false;
        this.releaseModelPhoto();

        this.isLoading = true;
        this.error = '';

        try {
            const response = await findInventoryItem(itemId);

            // Пока ехал ответ, открыли другой предмет: этот ответ уже никому не нужен.
            if (token !== this.itemLoadToken) {
                return;
            }

            // Ещё один сброс — непосредственно перед новым предметом. Пока карточка
            // грузилась, в шапку могло что-то лечь от прежнего; новый предмет начинает
            // с чистой шапки, и всё, что запрошено до этой строки, опоздало.
            this.primaryPhoto = null;
            this.primaryPhotoKnown = false;
            this.releaseModelPhoto();

            // Копия, а не ответ напрямую: форма правит объект на месте, и без копии
            // несохранённые изменения пережили бы закрытие панели.
            this.item = { ...response.data, attributes: [...(response.data.attributes ?? [])] };

            // Типовое фото — сразу, а не по слову вкладки «Файлы»: наличие главного
            // известно из самого ответа (primary_photo), и ждать вкладку значило бы
            // задержать вторую ступень цепочки на лишний запрос.
            this.ensureModelPhoto();
        } catch (requestError) {
            if (token !== this.itemLoadToken) {
                return;
            }

            this.item = null;
            this.error = errorMessage(requestError);
        } finally {
            // Загрузку завершает только текущее чтение: опоздавшее убрало бы индикатор,
            // пока открытый предмет ещё едет.
            if (token === this.itemLoadToken) {
                this.isLoading = false;
            }
        }
    }

    async save() {
        if (!this.item || this.isSaving || !this.canEditCard) {
            return;
        }

        this.isSaving = true;

        try {
            await updateInventoryItem(this.item);

            // Статуса эта форма больше не отправляет вовсе (7-F2B): он результат
            // бизнес-операции, а не поле карточки, и менять его сохранением означало бы
            // обходить и причину, и автора, и запись в деловой истории.

            // Держателя эта форма больше не отправляет вовсе (7-F2A): его меняют
            // бизнес-действия «Выдать», «Вернуть» и «Сменить владельца», и только они
            // оставляют в истории запись о том, кто и когда это сделал. Раньше отсюда
            // уходил PATCH держателя при КАЖДОМ сохранении карточки — даже когда
            // держателя никто не трогал.

            this.snapshot = null;
            this.isEditing = false;

            await this.fetchItem();
            this.afterChange();
        } catch (requestError) {
            this.$showModal(ModalNames.ERROR, { errorMessage: errorMessage(requestError) });
        } finally {
            this.isSaving = false;
        }
    }

    /**
     * Общий ход быстрого действия: запрос, перечитывание карточки, сообщение списку.
     *
     * Права проверяются здесь, а не только у кнопки: скрытая кнопка защищает от случайного
     * нажатия, а это — от вызова мимо неё. Тот же порядок, что у save().
     *
     * @param {Function} request
     */
    async runQuickAction(request) {
        if (!this.item || this.isSaving || !this.canEditItem) {
            return;
        }

        this.isSaving = true;

        try {
            await request();
            await this.fetchItem();
            this.afterChange();
        } catch (requestError) {
            this.$showModal(ModalNames.ERROR, { errorMessage: errorMessage(requestError) });
        } finally {
            this.isSaving = false;
        }
    }

    /**
     * Открыть окно бизнес-операции и применить её результат.
     *
     * ЗАПРОС ДЕЛАЕТ ОКНО, А НЕ КАРТОЧКА, и это не мелочь: отказ сервера должен приходить
     * в открытую форму, вместе с введённым текстом. Сюда возвращается уже обновлённая
     * карточка — тем же ответом, которым сервер подтвердил операцию.
     *
     * Права проверяются здесь, а не только у кнопки: скрытая кнопка защищает от
     * случайного нажатия, а это — от вызова мимо неё. Тот же порядок, что у save().
     *
     * @param {string} action
     */
    async runAction(action) {
        if (!this.item || this.isSaving || !this.canEditItem) {
            return;
        }

        try {
            const updated = await this.$showModal(this.actionModal(action), this.actionParams(action));

            this.applyActionResult(updated);
        } catch (error) {
            // Окно закрыли — ничего не произошло.
        }
    }

    /**
     * Какое окно открывает действие.
     *
     * «Выдать» и «Сменить владельца» делят одно окно: форма у них буквально одна и та же,
     * а различаются они маршрутом на сервере. Две копии одного списка держателей однажды
     * разошлись бы.
     *
     * @param {string} action
     * @returns {string}
     */
    actionModal(action) {
        const modals = {
            [ACTION_RETURN]: ModalNames.INVENTORY_ITEM_RETURN,
            [ACTION_MOVE]: ModalNames.INVENTORY_ITEM_MOVE,
            [ACTION_SEND_TO_REPAIR]: ModalNames.INVENTORY_ITEM_REPAIR,
            [ACTION_RETURN_FROM_REPAIR]: ModalNames.INVENTORY_ITEM_REPAIR_RETURN,
            [ACTION_MARK_BROKEN]: ModalNames.INVENTORY_ITEM_MARK_BROKEN,
            [ACTION_CLEAR_FAULT]: ModalNames.INVENTORY_ITEM_CLEAR_FAULT,
            [ACTION_WRITE_OFF]: ModalNames.INVENTORY_ITEM_WRITE_OFF,
        };

        // Выдача и смена владельца делят одно окно: форма у них буквально одна и та же,
        // а различает их маршрут на сервере.
        return modals[action] ?? ModalNames.INVENTORY_ITEM_HOLDER_ACTION;
    }

    /**
     * Что окно должно знать о предмете.
     *
     * ТОЛЬКО ДЛЯ ПОКАЗА. Текущий держатель и город уходят в окно затем, чтобы человек
     * видел, из какого состояния он выходит; в запрос они не попадают — сервер читает
     * состояние сам, уже под блокировкой строки.
     *
     * @param {string} action
     * @returns {Object}
     */
    actionParams(action) {
        const base = { itemId: this.item.id };

        // Имя держателя нужно пяти окнам, и по разным поводам: возврат говорит, у кого
        // вещь забирают, ремонт и отметка о неисправности — что ответственный остаётся
        // прежним, возврат из ремонта и снятие отметки — к кому она вернётся.
        const needsHolderName = [
            ACTION_RETURN,
            ACTION_SEND_TO_REPAIR,
            ACTION_RETURN_FROM_REPAIR,
            ACTION_MARK_BROKEN,
            ACTION_CLEAR_FAULT,
        ];

        if (needsHolderName.indexOf(action) !== -1) {
            return { ...base, holderName: this.holderName };
        }

        if (action === ACTION_MOVE) {
            return { ...base, cityId: this.item.city_id ?? null, cityName: this.item.city?.name ?? '' };
        }

        if (action === ACTION_WRITE_OFF) {
            return base;
        }

        // Выдача зарезервированного: окно сразу предлагает того, под кого резервировали.
        // Это лишь начальный выбор — сервер держателя из формы не берёт на веру.
        const isReserved = Number(this.item.status) === InventoryItemStatusType.RESERVED;

        if (action === ACTION_ISSUE && isReserved && this.hasHolder) {
            return {
                ...base,
                action,
                holderType: this.item.holder_id ? 'external' : 'employee',
                holderId: this.item.holder_id || this.item.responsible_person_id,
            };
        }

        return { ...base, action };
    }

    /**
     * Карточка после успешной операции.
     *
     * БЕЗ ПЕРЕЗАГРУЗКИ СТРАНИЦЫ И БЕЗ ПОВТОРНОГО ЗАПРОСА ЗА ПРЕДМЕТОМ: обновлённая
     * карточка приехала ответом на само действие. Лента жизненного цикла перерисовывается
     * сменой ключа — она перечитывает себя сама, и это единственный запрос, который
     * здесь действительно нужен.
     *
     * @param {Object|null} updated Карточка из ответа действия.
     */
    applyActionResult(updated) {
        if (updated && updated.id) {
            this.item = { ...updated, attributes: [...(updated.attributes ?? [])] };
        }

        this.afterChange();
    }

    /**
     * Область доступа изменилась (перезагрузка справочников, новая сессия) — закрываем
     * открытую правку, если позиция больше не наша.
     *
     * Сохранить из устаревшей формы и так не дало бы: save() проверяет право заново,
     * а следом откажет бэкенд. Но держать открытую форму, из которой ничего не сохранить, —
     * худший из вариантов: пользователь узнает об отказе, только потеряв введённое.
     */
    @Watch('canEditItem')
    onEditRightsLost(canEdit) {
        if (!canEdit && this.isEditing) {
            this.cancelEditing();
        }
    }

    // Список живёт в соседнем компоненте и о панели не знает — сообщаем ему шиной,
    // иначе строка осталась бы со старыми данными до перезагрузки страницы.
    afterChange() {
        this.historyVersion += 1;
        this.$bus.emit('inventory:item-changed');
    }

    async fetchCache() {
        try {
            const [users, holders, attributes, sourceData] = await Promise.all([
                getAllInventoryUsers(1, '', 100000),
                paginateInventoryHolders(1, '', { per_page: 100000 }),
                getInventoryAttributeTypes({ withAttributes: true }),
                getInventorySourceData(),
            ]);

            this.cache.users = users.data.data.map((user) => ({
                id: user.id,
                displayName: user.enabled ? user.displayName : `${user.displayName} (откл.)`,
            }));
            this.cache.holders = holders.data.data;
            this.cache.attributes = attributes.data.data;
            this.cache.attributesMap = this.cache.attributes.reduce((carry, attributeType) => {
                carry[attributeType.type] = attributeType;
                return carry;
            }, {});
            this.cache.attributesTypes = this.cache.attributes.map((attribute) => ({
                label: attribute.type,
                value: attribute.type,
            }));
            this.cache.cities = sourceData.data.cities ?? [];

            // Область приходит тем же ответом, что и справочники, — отдельного запроса
            // ради неё нет. Кладём в хранилище: её спрашивают и форма полей, и кнопка
            // правки, и список в соседнем компоненте.
            this.SET_INVENTORY_SCOPE(sourceData.data.scope ?? null);
        } catch (requestError) {
            // Справочники нужны только селектам формы: без них карточку всё равно видно,
            // и заслонять её модалкой поверх панели не за что.
            console.warn(requestError);
        }
    }

    @Watch('$route.params.id')
    onRouteChange() {
        // Переключились на другой предмет — карточка снова открывается на чтение,
        // иначе незавершённая правка перенеслась бы на чужую запись.
        this.isEditing = false;
        this.snapshot = null;
        this.fetchItem();
    }

    // Справочники грузятся до первой отрисовки формы, а не параллельно ей: VAttribute
    // читает cache.attributesMap по ключу атрибута, и предмет, приехавший раньше
    // справочников, уронил бы карточку на первом же атрибуте.
    async created() {
        this.isLoading = true;

        try {
            await this.fetchCache();
            await this.fetchItem();
        } finally {
            this.isLoading = false;
        }
    }

    beforeDestroy() {
        this.releaseModelPhoto();
    }
}

export default InventoryItemPanel;
</script>
<style lang="scss">
// Карточка оборудования.
//
// КАРТОЧКА, А НЕ АНКЕТА. Раньше содержание карточки было одной вертикальной стопкой пар
// «подпись — значение»: всё одинаковой ширины, одинакового веса, читается сверху вниз
// и только целиком. Теперь у неё композиция: шапка отвечает, что открыто; верхняя зона
// делится на паспорт предмета слева и его недавнюю жизнь справа; ниже — блоки реквизитов
// сеткой, а не столбиком; внизу — действия.
//
// ИЕРАРХИЯ, А НЕ РАМКИ. Вес распределён: название устройства самое заметное, за ним имя
// ответственного и значения полей, дальше подписи блоков, ещё тише подписи полей и совсем
// тихо — даты и служебные приписки. Блоки группируются подложкой, а не линиями: линия
// делит, подложка собирает.
.inventory-panel {
    // Шкала цветов карточки: текст, вторичный, приглушённый, линия, подложка блока.
    --panel-text: #202124;
    --panel-body: #3c4043;
    --panel-muted: #5f6368;
    --panel-faint: #9aa0a6;
    --panel-line: #eceef0;
    --panel-surface: #f6f7f9;
    --panel-surface-line: #e8eaed;

    background: #fff;
    border-left: 1px solid #e3e5e8;
    overflow: hidden;

    // Единственная прокрутка карточки. Ни у вкладок, ни у истории своей нет.
    //
    // На телефоне карточка занимает экран целиком (список рядом прячется, см. App.vue),
    // и прокручиваться должен именно этот контейнер, а не страница под ним: у карточки
    // своя длина, и смешивать её с прокруткой списка нельзя. overscroll-behavior держит
    // жест внутри — дойдя до конца истории, палец не утягивает за собой всё приложение.
    &__inner {
        height: 100%;
        overflow-y: auto;
        overscroll-behavior-y: contain;
        -webkit-overflow-scrolling: touch;
        padding: 18px;
        // Низ карточки не должен упираться в нижнюю панель навигации и в индикатор Home.
        // Высоту самой панели резервирует оболочка приложения; здесь — безопасная зона.
        padding-bottom: calc(18px + env(safe-area-inset-bottom, 0px));
        box-sizing: border-box;
    }

    &__loader {
        display: flex;
        justify-content: center;
        padding: 48px 0;
    }

    &__error {
        margin: 24px 0;
        color: var(--color-red);
        font-size: 0.875rem;
    }

    // ШАПКА. Две зоны: слева идентичность предмета (превью, название, состояние),
    // справа действия и QR. Служебных реквизитов в шапке нет — номер, город и серийный
    // номер стоят в «Ключевой информации» ниже; их вторая копия мелким шрифтом делала
    // шапку плотной, но не сильной.
    //
    // ПЕРЕНОС ДВУХСТУПЕНЧАТЫЙ, и порядок ступеней важен. Сначала внутри левой зоны
    // превью уходит на строку выше названия — так на узкой панели сохраняется главное:
    // крупная плитка слева и правая колонка с QR. И только если не хватает и на это,
    // правая зона опускается под левую. Медиазапросами это не решается: ширина панели
    // задаётся в процентах области (28…50%), а не окна.
    &__hero {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        gap: 14px;
    }

    // Левая зона. Базис маленький нарочно: он только про то, когда зона уедет под правую,
    // а это должно случаться последним. Внутренний перенос срабатывает раньше и мягче.
    &__identity {
        flex: 1 1 140px;
        min-width: 0;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 14px;
    }

    // Название со статусом. Базис 110 — ширина, ниже которой название рвётся на обрывки
    // по два слова; до неё текст стоит рядом с превью, после — под ним.
    &__identity-text {
        flex: 1 1 110px;
        min-width: 0;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
    }

    // Правая зона: действия сверху, QR под ними, оба прижаты к правому краю.
    &__hero-side {
        flex: 0 0 auto;
        margin-left: auto;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 10px;
    }

    &__hero-actions {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        gap: 6px;
    }

    // Название — главный элемент карточки. Длинное имя переносится по словам,
    // а не режет соседей.
    &__title {
        margin: 0;
        font-size: 1.375rem;
        line-height: 1.75rem;
        font-weight: 600;
        color: var(--panel-text);
        word-break: break-word;
    }

    // Правка — вторичное действие: главное в карточке её содержание, а не кнопка.
    &__edit {
        padding: 7px 14px;
        font-size: 0.8125rem;
        line-height: 1rem;
        white-space: nowrap;
    }

    // Закрытие — единственный выход из карточки, и его должно находить глазами сразу.
    // Красным, но не залитым: заливка сделала бы из выхода самое яркое пятно шапки
    // и поспорила бы с названием. Заливка приходит на наведение.
    &__close {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border: 1px solid #f6cccc;
        border-radius: 8px;
        background: #fff5f5;
        color: var(--color-red);
        transition: background-color 0.2s, border-color 0.2s, color 0.2s;

        &:hover,
        &:focus-visible {
            background: var(--color-red);
            border-color: var(--color-red);
            color: #fff;
        }

        & svg {
            width: 12px;
            height: 12px;
        }
    }

    // СТАТУС — второй по важности элемент карточки после названия, и выглядеть он должен
    // соответственно: рост 34 точки, полужирный, цветная заливка и мягкая тень её же цвета.
    // Точка перед подписью — не украшение: она держит слева ту же роль, что индикатор
    // в списке, и связывает пилюлю со счётчиками статусов над таблицей.
    //
    // Цвета подложки и текста приходят из badgeStyle: цвет текста считается от яркости
    // подложки, потому что среди статусных цветов есть почти жёлтый.
    &__badge {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        max-width: 100%;
        height: 34px;
        padding: 0 16px;
        border-radius: 999px;
        background: var(--badge-color, #9aa0a6);
        color: var(--badge-text, #fff);
        font-size: 0.875rem;
        font-weight: 700;
        line-height: 1rem;
        letter-spacing: 0.01em;
        // Подпись не переносится: статусов шесть, все короткие, а перенос превратил бы
        // акцент в двухэтажный прямоугольник.
        white-space: nowrap;
        box-shadow: 0 4px 12px var(--badge-shadow, transparent);

        &::before {
            content: '';
            flex-shrink: 0;
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: currentColor;
            opacity: 0.7;
        }
    }

    // QR: плитка с кодом и кнопка под ней ровно той же ширины — один блок, а не
    // картинка со случайной ссылкой рядом. QR читают с экрана телефоном и печатают
    // наклейкой, обе задачи требуют размера: 128 точек снимаются камерой с расстояния
    // вытянутой руки.
    &__qr {
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 5px;
    }

    &__qr-frame {
        display: block;
        padding: 5px;
        border: 1px solid var(--panel-surface-line);
        border-radius: 10px;
        background: #fff;
        line-height: 0;

        &:hover {
            border-color: var(--color-green);
        }
    }

    &__qr-image {
        display: block;
        width: 128px;
        height: 128px;
    }

    // Скачивание — действие, а не подпись под картинкой: рамка, подложка, иконка
    // и площадь нажатия во всю ширину кода.
    &__qr-download {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 7px 8px;
        border: 1px solid var(--panel-surface-line);
        border-radius: 8px;
        background: var(--panel-surface);
        color: var(--panel-muted);
        font-size: 0.75rem;
        line-height: 1rem;
        white-space: nowrap;
        transition: background-color 0.2s, border-color 0.2s, color 0.2s;

        &:hover,
        &:focus-visible {
            border-color: var(--color-green);
            background: #f3fcf8;
            color: var(--color-green);
        }
    }

    &__qr-download-icon {
        flex-shrink: 0;
        width: 13px;
        height: 13px;
    }

    // ПРЕВЬЮ. Плитка ровно того же размера, что рамка QR, — две равные по массе плитки
    // держат шапку по краям, и ни одна не выглядит случайной. Внутри не иконка, а
    // иллюстрация вида техники: рисунки нарисованы под этот размер, см. images/inventory.
    //
    // ЭТО НЕ ФОТОГРАФИЯ И НЕ УЧЁТНОЕ ПОЛЕ. Фотографий в системе нет, поля «тип» тоже:
    // вид угадан по названию. Поэтому иллюстрация приглушённая — она помогает опознать
    // предмет боковым зрением, но не притворяется снимком конкретной единицы техники.
    &__preview {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 140px;
        height: 140px;
        box-sizing: border-box;
        border: 1px solid var(--panel-surface-line);
        border-radius: 16px;
        background: linear-gradient(180deg, #fff 0%, var(--panel-surface) 100%);
        color: var(--panel-muted);
    }

    &__preview-image {
        width: 104px;
        height: 104px;
    }

    // ФОТОГРАФИЯ ЗАНИМАЕТ ПЛИТКУ ЦЕЛИКОМ, в отличие от рисунка по виду техники: рисунок
    // нарисован под поле в 104 точки и в рамке со светлой подложкой читается как знак,
    // а снимок в такой же рамке выглядел бы уменьшенным и случайным. Композиция шапки
    // от этого не меняется — размер плитки тот же, что у рамки QR.
    &__preview_photo {
        padding: 0;
        overflow: hidden;
        background: #fff;
    }

    &__preview-photo {
        width: 100%;
        height: 100%;
        // cover, а не contain: кадры с телефона бывают и вертикальными, и горизонтальными,
        // и поля вокруг вписанного снимка выглядят как ошибка вёрстки. Обрезается край,
        // а не смысл: целиком кадр смотрят во вкладке «Файлы».
        object-fit: cover;
        display: block;
    }

    // Фото есть, но ещё едет. Плитка держит место и молчит: рисунок по виду техники
    // здесь мелькал бы при каждом открытии карточки и сменялся снимком.
    &__preview-pending {
        display: block;
        width: 104px;
        height: 104px;
    }

    // Вид не распознан — плитка «изображения нет»: пунктир и знак фотографии, а не
    // «что-нибудь похожее». Неверная картинка врёт увереннее пустой, а знак фотографии
    // нельзя принять за вид техники.
    &__preview_empty {
        border-style: dashed;
        background: #fff;
        color: var(--panel-faint);
    }

    &__preview-stub {
        width: 56px;
        height: 56px;
        opacity: 0.5;
    }

    // Навигация карточки: компактнее общей, подчёркивание только под самой вкладкой.
    // Вкладок две, и обе рабочие — механику переключения не трогаем, только вид.
    &__tabs {
        margin-top: 6px;
        height: auto;
        overflow: visible;

        .tabs-component-tabs {
            padding: 0;
        }

        .tabs-component-tab {
            height: auto;
        }

        .tabs-component-tab-a {
            padding: 8px 0 9px;
            margin-right: 20px;
            font-size: 0.8125rem;
            line-height: 1.125rem;
        }

        .tabs-component-tab:hover,
        .tabs-component-tab.is-active {
            .tabs-component-tab-a::after {
                left: 0;
                right: 0;
                bottom: -1px;
                height: 2px;
                border-radius: 2px 2px 0 0;
            }
        }

        // Панели вкладок в общем стиле растянуты на всю высоту и скрывают переполнение:
        // внутри карточки это дало бы вторую прокрутку и обрезанный низ.
        .tabs-component-panels,
        .tabs-component-panel {
            height: auto;
            overflow: visible;
        }
    }

    // ВЕРХНЯЯ ЗОНА ВКЛАДКИ: паспорт слева, события справа. Ширину панели медиазапросами
    // не измерить — 28…50% области, — поэтому колонки складываются переносом флекса,
    // как только перестают помещаться.
    &__columns {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        margin-top: 14px;
    }

    &__column {
        min-width: 0;

        &_summary {
            flex: 3 1 220px;
        }

        // События уже паспорта: это контекст, а не содержание карточки.
        &_events {
            flex: 2 1 190px;
        }
    }

    // Подложка группирует блок. Рамок в карточке нет: линия делит, подложка собирает.
    &__card {
        padding: 12px 14px;
        border-radius: 10px;
        background: var(--panel-surface);
        box-sizing: border-box;
    }

    &__card-head {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 8px;
    }

    &__card-link {
        flex-shrink: 0;
        padding: 0;
        border: 0;
        background: none;
        color: var(--color-green);
        font-size: 0.75rem;
        line-height: 1rem;
        white-space: nowrap;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }

    &__section {
        margin-top: 16px;

        &_actions {
            margin-top: 20px;
            padding-top: 14px;
            border-top: 1px solid var(--panel-line);
        }
    }

    // Подпись блока: спокойная, обычным регистром, чуть крупнее подписи поля.
    &__section-title {
        margin: 0 0 8px 0;
        font-size: 0.8125rem;
        font-weight: 600;
        line-height: 1.125rem;
        color: var(--panel-text);
    }

    // Заголовок блока ВНУТРИ ВКЛАДКИ — отдельный класс, а не section-title. Тот метит
    // блоки «Основного», и подмешивать к ним заголовки других вкладок значит смешивать
    // два разных уровня в одной выборке — и в вёрстке, и в тестах.
    &__tab-title {
        margin: 0 0 4px 0;
        font-size: 0.9375rem;
        font-weight: 600;
        line-height: 1.25rem;
        color: var(--panel-text);
    }

    &__timeline {
        display: block;
    }

    // Техническая история — второй уровень внимания: отделена чертой и отступом,
    // чтобы деловая лента выше читалась как главное содержимое вкладки.
    &__audit {
        display: block;
        margin-top: 20px;
        padding-top: 12px;
        border-top: 1px solid #eef1f4;
    }

    // Кнопка на всю ширину: раскрывать блок нажатием в любое место строки удобнее,
    // чем целиться в треугольник, — особенно пальцем.
    &__audit-toggle {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        min-height: 36px;
        padding: 0;
        border: 0;
        background: transparent;
        color: #5f6368;
        font-size: 0.8125rem;
        font-weight: 500;
        text-align: left;
        cursor: pointer;

        &:hover {
            color: #202124;
        }
    }

    &__audit-caret {
        color: #9aa0a6;
        font-size: 0.75rem;
        line-height: 1;
    }

    // Паспорт предмета: подпись слева, значение справа. Строки выровнены по правому краю —
    // так значения читаются столбцом, а не выискиваются среди подписей.
    &__summary {
        margin: 0;
    }

    &__summary-row {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 10px;
        padding: 5px 0;
        border-bottom: 1px solid var(--panel-surface-line);

        &:last-child {
            padding-bottom: 0;
            border-bottom: 0;
        }

        &:first-child {
            padding-top: 0;
        }
    }

    &__summary-name {
        flex-shrink: 0;
        margin: 0;
        color: var(--panel-faint);
        font-size: 0.75rem;
        line-height: 1.0625rem;
    }

    &__summary-value {
        min-width: 0;
        margin: 0;
        color: var(--panel-body);
        font-size: 0.8125rem;
        font-weight: 500;
        line-height: 1.0625rem;
        text-align: right;
        word-break: break-word;
    }

    // Реквизиты сеткой, а не столбиком: три значения поставки занимают одну полосу,
    // а не треть высоты карточки. Колонки считает сама сетка.
    &__facts {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
        gap: 10px 14px;
        margin: 0;

        &_tight {
            grid-template-columns: repeat(auto-fit, minmax(115px, 1fr));
        }
    }

    &__fact {
        min-width: 0;
    }

    &__fact-name {
        margin: 0;
        color: var(--panel-faint);
        font-size: 0.6875rem;
        line-height: 0.9375rem;
    }

    &__fact-value {
        margin: 1px 0 0 0;
        color: var(--panel-body);
        font-size: 0.8125rem;
        font-weight: 500;
        line-height: 1.125rem;
        word-break: break-word;
    }

    // Приписка к значению — истёкшая гарантия. Вторичная: это уточнение, а не авария,
    // и кричать красным ей незачем.
    &__fact-note {
        margin-left: 6px;
        color: #a06000;
        font-size: 0.6875rem;
        font-weight: 400;
        white-space: nowrap;
    }

    // Ответственный читается за секунду: аватар держит левый край, имя крупнее значений
    // полей, всё остальное про него — тише и ниже.
    &__holder-head {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    &__holder-avatar {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 34px;
        height: 34px;
        border-radius: 50%;
        background: #e4efe8;
        color: #1f7a4d;
        font-size: 0.75rem;
        font-weight: 600;
        line-height: 1rem;
        letter-spacing: 0.02em;
    }

    &__holder-box {
        min-width: 0;
    }

    &__holder-name {
        margin: 0;
        color: var(--panel-text);
        font-size: 0.9375rem;
        font-weight: 600;
        line-height: 1.25rem;
        word-break: break-word;

        // «Не назначен» — состояние, а не пустота: тише имени, но не серым по серому.
        &_empty {
            color: var(--panel-muted);
            font-weight: 500;
        }
    }

    &__holder-kind {
        margin: 2px 0 0 0;
        color: var(--panel-faint);
        font-size: 0.75rem;
        line-height: 1rem;
    }

    &__holder-flag {
        color: #a06000;
    }

    .inventory-panel__holder .inventory-panel__facts {
        margin-top: 12px;
        padding-top: 10px;
        border-top: 1px solid var(--panel-surface-line);
    }

    // Свободный текст атрибута: назначение, комментарий. Читается абзацем, а не значением
    // поля, поэтому во всю ширину и с переносами.
    &__text {
        margin: 0;
        color: var(--panel-body);
        font-size: 0.8125rem;
        line-height: 1.25rem;
        white-space: pre-line;
        word-break: break-word;
    }

    // Красная кнопка необратимого действия. Своя, а не общий модификатор: такого
    // в проекте нет, а заводить общий ради одного места значит договариваться за всех.
    &__quick-button_danger {
        border-color: #f3c0bc;
        background: #fdecea;
        color: #b3261e;

        &:hover:not(:disabled) {
            border-color: #d93025;
            background: #fadbd8;
        }
    }

    // Быстрые действия: только то, что у системы уже есть отдельным маршрутом.
    &__quick {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    &__quick-label {
        flex-shrink: 0;
        color: var(--panel-faint);
        font-size: 0.75rem;
        line-height: 1rem;
    }

    // Селект в строке действий компактнее формы: он здесь не поле ввода, а кнопка с выбором.
    &__quick-status {
        flex: 1 1 auto;
        min-width: 0;
        max-width: 220px;

        &.v-select .vs__dropdown-toggle {
            padding: 7px 10px;
            border-radius: 8px;
            background: #fff;
        }

        &.v-select .vs__selected {
            font-size: 0.8125rem;
        }
    }

    &__quick-buttons {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 10px;
    }

    // Быстрые действия заметнее прочих кнопок карточки: иконка слева, площадь нажатия
    // больше, при наведении — зелёная рамка. Их ищут глазами, а не читают подряд.
    &__quick-button {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 8px 14px;
        border: 1px solid var(--panel-surface-line);
        font-size: 0.8125rem;
        line-height: 1.125rem;
        font-weight: 500;

        &:hover:not(:disabled) {
            border-color: var(--color-green);
        }
    }

    &__quick-icon {
        flex-shrink: 0;
        width: 15px;
        height: 15px;
    }

    &__actions {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 20px;
        padding-top: 14px;
        border-top: 1px solid var(--panel-line);
    }
}
</style>
